import * as React from 'react';
import { Auftrag } from 'api/auftrag';
import { AuftragPosition, changeAuftragPositionMenge, useAuftragPositionList } from 'api/auftragPosition';
import { LieferArt } from 'formComponents/lookups/LieferArt';
import { TextField } from '@fluentui/react/lib/TextField';
import { formatDecimal, parseDecimal } from 'utils/formatter';
import { formStyles } from 'styles/formStyles';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { useArtikelDetail } from 'api/artikelDetail';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { artikelGruppenByNrSelector } from 'store/lookups/selector';
import { useSelector } from 'react-redux';

const styles = mergeStyleSets({
    changeMengeGrid: {
        marginTop: 20,
        marginBottom: 20,
        display: 'grid',
        gridTemplateColumns: '120px 120px 120px 120px 120px 120px',
        gridRowGap: 10,
        gridColumnGap: 10,
    },
    mengeLabel: {
        marginTop: 6,
    },
    menge: {
        textAlign: 'right',
    },
});

interface ChangeMenge {
    error: string | null;
    newMenge: number;
    newMengeS: string;
    newMengeDifferenceS: string;
    newOffen: number;
    newOffenS: string;
    newOffenDifferenceS: string;
    newLArt: string;
}

const defaultChangeMenge: ChangeMenge = {
    error: null,
    newMenge: 0,
    newMengeS: '0',
    newMengeDifferenceS: '0',
    newOffen: 0,
    newOffenS: '0',
    newOffenDifferenceS: '0',
    newLArt: '',
};

interface AuftragPositionChangeMengeProps {
    auftrag: Auftrag;
    auftragPosition: AuftragPosition;
    onClose: (refetch: boolean) => void;
}

export const AuftragPositionChangeMenge: React.FC<AuftragPositionChangeMengeProps> = ({
    auftrag,
    auftragPosition,
    onClose,
}) => {
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
    const [changeMenge, setChangeMenge] = React.useState({
        ...defaultChangeMenge,
        newMenge: auftragPosition.posMenge,
        newMengeS: formatDecimal(auftragPosition.posMenge, 3),
        newMengeDifferenceS: formatDecimal(0, 3),
        newOffen: auftragPosition.offen,
        newOffenS: formatDecimal(auftragPosition.offen, 3),
        newOffenDifferenceS: formatDecimal(0, 3),
        newLArt: auftragPosition.lArt,
    });
    const [rahmenAuftragMenge, setRahmenAuftragMenge] = React.useState({ posMenge: 0, offen: 0 });

    const rahmenAuftragPositionList = useAuftragPositionList(
        {
            auNr: auftrag.abrufZu,
        },
        { enabled: !!auftrag.abrufZu },
    );

    const artikelDetail = useArtikelDetail({
        artikelGruppeNr: auftragPosition.artikelGruppeNr,
        artikelNr: auftragPosition.artikelNr,
        includeArchiv: true,
    });

    React.useEffect(() => {
        if (auftrag.abrufZu && auftragPosition && rahmenAuftragPositionList.data) {
            let posMenge = 0;
            let offen = 0;
            rahmenAuftragPositionList.data
                .filter(
                    (rap) =>
                        rap.artikelGruppeNr === auftragPosition.artikelGruppeNr &&
                        rap.artikelNr === auftragPosition.artikelNr,
                )
                .forEach((rap) => {
                    posMenge += rap.posMenge;
                    offen += rap.offen;
                });
            setRahmenAuftragMenge({ posMenge, offen });
        }
    }, [auftrag.abrufZu, auftragPosition, auftragPosition.pos, rahmenAuftragPositionList]);

    const setValidatedChangeMengeLArt = (newMenge: number, newOffen: number, newLArt: string, updateOffen = false) => {
        let error: string | null = null;
        if (newMenge < 0) {
            error = 'Menge darf nicht < 0 sein.';
        }

        let updatedNewOffen = newOffen;

        if (updateOffen) {
            updatedNewOffen = auftragPosition.offen + newMenge - auftragPosition.posMenge;
        }

        if (updatedNewOffen < 0) {
            error = 'Offen darf nicht < 0 sein.';
        }

        setChangeMenge({
            error,
            newMenge,
            newMengeS: formatDecimal(newMenge, 3),
            newMengeDifferenceS: formatDecimal(newMenge - (auftragPosition?.posMenge || 0), 3),
            newOffen: updatedNewOffen,
            newOffenS: formatDecimal(updatedNewOffen, 3),
            newOffenDifferenceS: formatDecimal(updatedNewOffen - (auftragPosition?.offen || 0), 3),
            newLArt,
        });
    };

    return (
        <ConfirmMessage
            title="Lieferart / Menge ändern"
            message={`Lieferart / Menge des Artikels ${artikelGruppenByNr[auftragPosition?.artikelGruppeNr]?.text} | ${
                auftragPosition?.artikelNr
            } ändern?`}
            confirmText="Lieferart / Menge ändern"
            cancelText="Abbruch"
            minWidth={820}
            content={
                <>
                    <ul>
                        {auftrag.abrufZu && <li>Die offene Menge am Rahmenauftrag wird angepasst.</li>}
                        <li>
                            {/* eslint-disable-next-line max-len */}
                            Wird die Lieferart von <strong>Reserviert</strong>/<strong>Reserviert für Rahmen</strong>{' '}
                            auf {/* eslint-disable-next-line max-len */}
                            <strong>Direkt</strong>/<strong>Lager</strong>/<strong>Leer</strong> geändert, wird{' '}
                            <strong>Reservierungen</strong> in der Lagerverwaltung reduziert.
                        </li>
                        <li>
                            Wird die Lieferart von <strong>Direkt</strong>/<strong>Lager</strong>/
                            {/* eslint-disable-next-line max-len */}
                            <strong>Leer</strong> auf <strong>Reserviert</strong>/<strong>Reserviert für Rahmen</strong>{' '}
                            geändert, wird <strong>Reservierungen</strong> in der Lagerverwaltung erhöht.
                        </li>
                        <li>
                            Der <strong>Bestand</strong> in der Lagerverwaltung wird in keinem Fall verändert.
                        </li>
                    </ul>
                    {rahmenAuftragPositionList.isLoading || artikelDetail.isLoading ? (
                        <LoadingIndicator />
                    ) : (
                        <div className={styles.changeMengeGrid}>
                            {auftrag.abrufZu && (
                                <>
                                    <TextField
                                        label="Gesamtmenge am Rahmenauftrag"
                                        inputClassName={styles.menge}
                                        readOnly
                                        disabled
                                        value={formatDecimal(rahmenAuftragMenge.posMenge, 3)}
                                    />
                                    <TextField
                                        label="Offene Menge am Rahmenauftrag"
                                        inputClassName={styles.menge}
                                        readOnly
                                        disabled
                                        value={formatDecimal(rahmenAuftragMenge.offen, 3)}
                                    />
                                    <div />
                                    <div />
                                </>
                            )}

                            <TextField
                                label="Lagerbestand"
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(artikelDetail.data?.bestand || 0, 3)}
                            />
                            <TextField
                                label="offene Lief."
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(artikelDetail.data?.offenLief || 0, 3)}
                            />
                            <TextField
                                label="offene Lief. (D)"
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(artikelDetail.data?.offenLiefD || 0, 3)}
                            />
                            <TextField
                                label="offene Best."
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(artikelDetail.data?.offenBest || 0, 3)}
                            />
                            <TextField
                                label="offene Best. (D)"
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(artikelDetail.data?.offenBestD || 0, 3)}
                            />
                            <TextField
                                label="Reservierungen"
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(artikelDetail.data?.offenRes || 0, 3)}
                            />

                            <div>
                                <LieferArt
                                    label="Aktuelle Lieferart"
                                    disabled
                                    allowEmpty
                                    value={auftragPosition?.lArt || ''}
                                />
                            </div>
                            <div>
                                <LieferArt
                                    label="Neue Lieferart"
                                    allowEmpty
                                    value={changeMenge.newLArt || ''}
                                    menuPlacement="top"
                                    onChange={(newValue) => {
                                        setValidatedChangeMengeLArt(
                                            changeMenge.newMenge,
                                            changeMenge.newOffen,
                                            newValue,
                                        );
                                    }}
                                />
                            </div>
                            <div />
                            <div />
                            <div />
                            <div />

                            <TextField
                                label="Aktuelle Menge"
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(auftragPosition?.posMenge || 0, 3)}
                            />
                            <TextField
                                label="Neue Menge"
                                inputClassName={styles.menge}
                                value={changeMenge.newMengeS}
                                onChange={(event, newValue) =>
                                    setChangeMenge({
                                        ...changeMenge,
                                        newMengeS: newValue === undefined ? '' : newValue,
                                    })
                                }
                                onBlur={(event): void => {
                                    setValidatedChangeMengeLArt(
                                        parseDecimal(event.target.value || '') || 0,
                                        changeMenge.newOffen,
                                        changeMenge.newLArt,
                                        true,
                                    );
                                }}
                            />
                            <div>
                                <TextField
                                    label="Differenz"
                                    inputClassName={styles.menge}
                                    value={changeMenge.newMengeDifferenceS}
                                    onChange={(event, newValue) =>
                                        setChangeMenge({
                                            ...changeMenge,
                                            newMengeDifferenceS: newValue === undefined ? '' : newValue,
                                        })
                                    }
                                    onBlur={(event): void => {
                                        setValidatedChangeMengeLArt(
                                            (auftragPosition?.posMenge || 0) + (parseDecimal(event.target.value) || 0),
                                            changeMenge.newOffen,
                                            changeMenge.newLArt,
                                            true,
                                        );
                                    }}
                                />
                            </div>
                            <div />
                            <div />
                            <div />

                            <TextField
                                label="Aktuell Offen"
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(auftragPosition?.offen || 0, 3)}
                            />
                            <TextField
                                label="Neu Offen"
                                inputClassName={styles.menge}
                                value={changeMenge.newOffenS}
                                onChange={(event, newValue) =>
                                    setChangeMenge({
                                        ...changeMenge,
                                        newOffenS: newValue === undefined ? '' : newValue,
                                    })
                                }
                                onBlur={(event): void => {
                                    setValidatedChangeMengeLArt(
                                        changeMenge.newMenge,
                                        parseDecimal(event.target.value || '') || 0,
                                        changeMenge.newLArt,
                                    );
                                }}
                            />
                            <div>
                                <TextField
                                    label="Differenz"
                                    inputClassName={styles.menge}
                                    value={changeMenge.newOffenDifferenceS}
                                    onChange={(event, newValue) =>
                                        setChangeMenge({
                                            ...changeMenge,
                                            newOffenDifferenceS: newValue === undefined ? '' : newValue,
                                        })
                                    }
                                    onBlur={(event): void => {
                                        setValidatedChangeMengeLArt(
                                            changeMenge.newMenge,
                                            (auftragPosition?.offen || 0) + (parseDecimal(event.target.value) || 0),
                                            changeMenge.newLArt,
                                        );
                                    }}
                                />
                            </div>
                            <div />
                        </div>
                    )}
                    {changeMenge.error && <div className={formStyles.error}>{changeMenge.error}</div>}
                </>
            }
            onConfirm={async () => {
                await changeAuftragPositionMenge({
                    ...auftragPosition,
                    posMenge: changeMenge.newMenge,
                    // offen: auftragPosition.offen + changeMenge.newMenge - auftragPosition.posMenge,
                    offen: changeMenge.newOffen,
                    lArt: changeMenge.newLArt,
                }).then(() => {
                    onClose(true);
                });
            }}
            confirmDisabled={
                !!changeMenge.error ||
                (changeMenge.newMenge === auftragPosition?.posMenge &&
                    changeMenge.newOffen === auftragPosition?.offen &&
                    changeMenge.newLArt === auftragPosition?.lArt)
            }
            onCancel={() => onClose(false)}
        />
    );
};
